import { useEffect, useRef, type KeyboardEvent } from "react";

import type { MatchSetupSelection } from "./MatchSetup";
import { MatchSetup } from "./MatchSetup";

import "./ContinueMatchDialog.css";

export type ContinueMatchDialogProps = Readonly<{
  initialSelection: MatchSetupSelection;
  discMarkers: boolean;
  replacingActiveMatch: boolean;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onContinue: (selection: MatchSetupSelection) => void;
}>;

export function ContinueMatchDialog({
  initialSelection,
  discMarkers,
  replacingActiveMatch,
  busy,
  error,
  onCancel,
  onContinue,
}: ContinueMatchDialogProps) {
  const dialog = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const focusOrigin = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialog.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (focusOrigin instanceof HTMLElement && focusOrigin.isConnected) focusOrigin.focus();
    };
  }, []);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Escape" && !busy) {
      event.preventDefault();
      onCancel();
      return;
    }
    if (event.key !== "Tab") return;
    const focusable = focusableElements(dialog.current);
    const first = focusable[0];
    const last = focusable.at(-1);
    if (first === undefined || last === undefined) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const description = replacingActiveMatch
    ? "Choose who controls this position. Continuing replaces the unfinished game on this device; completed history remains unchanged."
    : "Choose who controls this position. The original completed match remains unchanged in history.";

  return (
    <div
      className="continue-match-dialog__backdrop"
      onMouseDown={(event) => {
        if (!busy && event.target === event.currentTarget) onCancel();
      }}
    >
      <div
        aria-label="Continue from this position"
        aria-modal="true"
        className="continue-match-dialog"
        onKeyDown={handleKeyDown}
        ref={dialog}
        role="dialog"
        tabIndex={-1}
      >
        <MatchSetup
          cancelLabel="Cancel"
          description={description}
          disabled={busy}
          discMarkers={discMarkers}
          initialSelection={initialSelection}
          onCancel={onCancel}
          onStart={onContinue}
          submitLabel={busy ? "Starting…" : "Continue game"}
          title="Continue from here"
        />
        {error === null ? null : (
          <p className="continue-match-dialog__error" role="alert">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}

function focusableElements(container: HTMLElement | null): readonly HTMLElement[] {
  if (container === null) return [];
  return [
    ...container.querySelectorAll<HTMLElement>(
      "button:not([disabled]), input:not([disabled]), [href], [tabindex]",
    ),
  ].filter((element) => element !== container && element.tabIndex >= 0);
}
